import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, LogOut, BookOpen, GraduationCap, Loader2 } from "lucide-react";
import { log } from "@/lib/log";

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_LABELS = {
  kit_digital: { icon: BookOpen, label: "Kit Digital Premium" },
  coaching_elite: { icon: GraduationCap, label: "Coaching Élite — Triple Fusion™" },
  coaching_souverain: { icon: Crown, label: "Coaching Souverain — Triple Fusion™" },
  legacy_purchase: { icon: BookOpen, label: "Accès membre" },
};

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/portal/me`, { credentials: "include" });
        const data = await res.json();
        if (!data.authenticated) {
          navigate("/espace?err=invalid_or_expired", { replace: true });
          return;
        }
        setMe(data);
      } catch {
        navigate("/espace?err=invalid_or_expired", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/portal/logout`, { method: "POST", credentials: "include" });
    } catch (e) { log.warn("[portal] logout serveur ignoré :", e); }
    navigate("/espace", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-28 pb-24 px-6" data-testid="portal-dashboard-loading">
        <Loader2 className="text-gold animate-spin" size={32} strokeWidth={1.5} />
      </div>
    );
  }

  if (!me) return null;

  const firstName = (me.name || "").split(" ")[0] || (me.email || "").split("@")[0];

  return (
    <div className="min-h-screen pt-28 pb-24 px-6" data-testid="portal-dashboard-page">
      <div className="max-w-4xl mx-auto">
        <motion.header
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="flex items-start justify-between mb-16"
        >
          <div>
            <p className="text-xs uppercase tracking-luxe text-gold mb-3">Mon Espace</p>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-[1.05]">
              Bienvenue{firstName ? `, ${firstName}` : ""}.
            </h1>
            <p className="text-white/50 font-light mt-3 text-sm">{me.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="portal-logout"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/60 hover:text-gold border border-white/15 hover:border-gold px-4 py-2 transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} /> Déconnexion
          </button>
        </motion.header>

        <motion.section
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          data-testid="portal-products"
        >
          <p className="text-xs uppercase tracking-luxe text-gold mb-6">Vos programmes actifs</p>
          <div className="grid md:grid-cols-2 gap-4">
            {(me.products || []).map((p) => {
              const cfg = PRODUCT_LABELS[p] || { icon: BookOpen, label: p };
              const Icon = cfg.icon;
              return (
                <div key={p} className="p-6 border border-white/10 bg-brand-surface/30 flex items-start gap-4" data-testid={`portal-product-${p}`}>
                  <Icon className="text-gold shrink-0 mt-1" size={22} strokeWidth={1.5} />
                  <div>
                    <p className="text-white font-serif text-lg leading-tight">{cfg.label}</p>
                    <p className="text-white/40 text-xs mt-1 uppercase tracking-[0.15em]">Accès actif</p>
                  </div>
                </div>
              );
            })}
            {(!me.products || me.products.length === 0) && (
              <p className="text-white/50 font-light">Aucun programme rattaché pour le moment.</p>
            )}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 p-6 border border-white/10 bg-brand-surface/20"
        >
          <p className="text-white/60 font-light text-sm leading-relaxed">
            Votre espace membre s'enrichira progressivement — accès aux enregistrements de séances, ressources exclusives et
            calendrier de rendez-vous. Pour toute question, écrivez à{" "}
            <a href="mailto:gestion.svh@serenite-essence.fr" className="text-gold hover:underline">gestion.svh@serenite-essence.fr</a>.
          </p>
        </motion.section>
      </div>
    </div>
  );
}
